import { BlankSlate } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { ContentCard } from "@msuite/ui/content-card";
import { Draggable } from "@msuite/ui/dnd";
import { TextInput } from "@msuite/ui/form";
import { useFilter, useSearchForm } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useMemo } from "react";
import { UserCard } from "@/components/cards";
import { useDaily } from "@/components/plan/daily/use-daily";
import { usePlanStore } from "@/components/plan/store";
import { useMembers } from "@/hooks/use-members";

export function UserResources() {
	/** Store */
	const activeDate = usePlanStore((state) => state.activeDate);

	/** Hooks */
	const { members, refetch, isFetching } = useMembers({ capabilities: ["crew-scheduling"] });
	const { t } = useTranslation();
	const { plan, guestPlans } = useDaily(activeDate.db());
	const searchForm = useSearchForm("member-resources");

	/** Computed */
	// Everyone the date already holds, and a date holds more than one board: a crew of ours
	// standing on a partner's board hangs off *their* `plans` row, so our own plan never mentions
	// the people in it. Reading our board alone offers those people here as free and books them a
	// second time for the same day. Partner columns on our own board are walked too — their
	// staffing is the partner's own people, who never reach this list, so the wider walk costs
	// nothing and misses nothing.
	const bookedUserIds = useMemo(() => {
		const ids = new Set<string>();
		for (const crew of plan?.crews ?? []) {
			for (const assignment of crew.userAssignments) {
				ids.add(assignment.userId);
			}
		}
		for (const guestPlan of guestPlans ?? []) {
			for (const crew of guestPlan.crews) {
				for (const assignment of crew.userAssignments) {
					ids.add(assignment.userId);
				}
			}
		}
		// A driver on the day's vehicle list is booked too, though no crew holds them. Only our own
		// board contributes one: a guest board's vehicles belong to the host and never sync here.
		for (const assignment of plan?.vehicleAssignmentsPlan ?? []) {
			if (assignment.userId) ids.add(assignment.userId);
		}
		return ids;
	}, [plan?.crews, plan?.vehicleAssignmentsPlan, guestPlans]);

	const availableMembers = useMemo(() => {
		return members.filter((member) => !bookedUserIds.has(member.user.id));
	}, [members, bookedUserIds]);

	const { filtered: filteredAvailableMembers } = useFilter(
		availableMembers,
		searchForm.searchValue,
	);

	/** Render */
	return (
		<ContentCard
			title={t("users.users")}
			icon={icon("User")}
			contentClassName="v-stack flex-1 min-h-0 overflow-y-auto"
			className="h-full overflow-hidden"
			variant="plain"
		>
			<div className="h-stack">
				<TextInput {...searchForm.getSearchInputProps()} />
				<Button
					icon={<Icon name="RefreshCcw" />}
					size="sm"
					onClick={refetch}
					isLoading={isFetching}
				/>
			</div>
			{!filteredAvailableMembers.length && <BlankSlate withBorder />}
			{filteredAvailableMembers?.map((member) => (
				<Draggable
					key={member.user.id}
					item={member.user}
					type="user"
				>
					<UserCard
						user={member.user}
						userId={member.user.id}
						absenceTargetDate={activeDate.db()}
					/>
				</Draggable>
			))}
		</ContentCard>
	);
}
